class BlogOverview extends HTMLElement {
  constructor() {
    super();

    this.currentQuery = '';
    this.currentPage = 1;

    this.handleSearch = this.handleSearch.bind(this);
  }

  connectedCallback() {
    this.cacheElements();
    this.bindFilterToggle();

    if (!this.cards.length) {
      return;
    }

    this.bindEvents();
    this.applyState();
  }

  cacheElements() {
    this.perPage = Number.parseInt(this.dataset.postsPerPage, 10) || 15;
    this.gridItems = Array.from(this.querySelectorAll('.blog-overview__grid-item'));
    this.cards = Array.from(this.querySelectorAll('[data-blog-card]'));
    this.searchInput = this.querySelector('[data-blog-search]');
    this.emptyState = this.querySelector('[data-blog-empty-state]');
    this.pagination = this.querySelector('[data-blog-pagination]');
    this.paginationList = this.querySelector('[data-blog-pagination-list]');
    this.filters = this.querySelector('[data-blog-filters]');
    this.filtersToggle = this.querySelector('[data-blog-filters-toggle]');
  }

  bindFilterToggle() {
    if (!this.filters || !this.filtersToggle) {
      return;
    }

    this.filtersToggle.addEventListener('click', () => {
      const expanded = this.filtersToggle.getAttribute('aria-expanded') === 'true';

      this.filtersToggle.setAttribute('aria-expanded', String(!expanded));
      this.filters.classList.toggle('is-open', !expanded);
    });
  }

  bindEvents() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.handleSearch);
    }

    if (this.paginationList) {
      this.paginationList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-page]');

        if (!button) {
          return;
        }

        const page = Number.parseInt(button.dataset.page, 10);

        if (!Number.isNaN(page) && page !== this.currentPage) {
          this.currentPage = page;
          this.applyState();
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  }

  handleSearch(event) {
    this.currentQuery = event.target.value.trim().toLowerCase();
    this.currentPage = 1;
    this.applyState();
  }

  getFilteredIndexes() {
    return this.cards
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => {
        if (!this.currentQuery) {
          return true;
        }

        const categoryLabels = card.dataset.categoryLabels || '';
        const title = card.dataset.title || '';

        return title.includes(this.currentQuery) || categoryLabels.includes(this.currentQuery);
      })
      .map(({ index }) => index);
  }

  applyState() {
    const filteredIndexes = this.getFilteredIndexes();
    const totalPages = Math.max(1, Math.ceil(filteredIndexes.length / this.perPage));

    if (this.currentPage > totalPages) {
      this.currentPage = 1;
    }

    const start = (this.currentPage - 1) * this.perPage;
    const end = start + this.perPage;
    const visibleIndexes = new Set(filteredIndexes.slice(start, end));

    this.gridItems.forEach((item, index) => {
      item.hidden = !visibleIndexes.has(index);
    });

    if (this.emptyState) {
      this.emptyState.hidden = filteredIndexes.length > 0;
    }

    this.renderPagination(totalPages);
  }

  renderPagination(totalPages) {
    if (!this.pagination || !this.paginationList) {
      return;
    }

    if (totalPages <= 1) {
      this.pagination.hidden = true;
      this.paginationList.innerHTML = '';
      return;
    }

    this.pagination.hidden = false;
    this.paginationList.innerHTML = '';

    const pageLabelTemplate = this.paginationList.dataset.pageLabelTemplate || 'Page [number]';
    const model = this.getPaginationModel(totalPages);

    model.forEach((part) => {
      const listItem = document.createElement('li');

      if (part === '...') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'blog-overview__pagination-ellipsis';
        ellipsis.setAttribute('aria-hidden', 'true');
        ellipsis.textContent = '...';
        listItem.appendChild(ellipsis);
      } else {
        const button = document.createElement('button');
        const isCurrent = part === this.currentPage;

        button.type = 'button';
        button.className = 'blog-overview__pagination-button';
        button.dataset.page = String(part);
        button.setAttribute('aria-label', pageLabelTemplate.replace('[number]', String(part)));
        button.textContent = String(part);

        if (isCurrent) {
          button.classList.add('is-active');
          button.setAttribute('aria-current', 'page');
        }

        listItem.appendChild(button);
      }

      this.paginationList.appendChild(listItem);
    });
  }

  getPaginationModel(totalPages) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
    const normalizedPages = Array.from(pages)
      .filter((page) => page > 0 && page <= totalPages)
      .sort((a, b) => a - b);

    const model = [];

    normalizedPages.forEach((page, index) => {
      if (index > 0 && page - normalizedPages[index - 1] > 1) {
        model.push('...');
      }

      model.push(page);
    });

    return model;
  }
}

if (!customElements.get('blog-overview')) {
  customElements.define('blog-overview', BlogOverview);
}
